<script setup>
import ToastComponent from '@/components/ToastComponent.vue';
import { useAxios } from '@/hooks/useAxios';
import { useCommonCodeStore } from '@/store/commoncode-store';
import { computed, nextTick, onMounted, ref, watch } from 'vue';

const props = defineProps({
    modelValue: { type: [String, Number], default: '' },
    inputId: { type: String, default: '' },
    label: { type: String, default: '' },
    required: { type: Boolean, default: false },
    placeholder: { type: String, default: '' },
    disabled: { type: Boolean, default: false },
    marginTop: { type: String, default: '0' },
    marginBottom: { type: String, default: '0' },
    marginLeft: { type: String, default: '0' },
    marginRight: { type: String, default: '0' },
    labelRightmargin: { type: String, default: '2rem' },
    visible: { type: Boolean, default: true },
    maxlength: { type: Number, default: 4000 },
    validationCheck: { type: Function, default: () => ({ isValid: true, message: '' }) },
    readonly: { type: Boolean, default: false },
    errmsg: { type: String, default: '' },
    labelEmpty: { type: Boolean, default: false },
    selectedVehicleType: { type: String, default: '' },
    selectedVehicleModel: { type: String, default: '' },
    contractNo: { type: String, default: '' },
    chargeRatio: { type: [String, Number], default: '' },
    vehicle: { type: Object, default: () => ({}) }
});

const emit = defineEmits(['update:modelValue', 'input', 'click', 'change']);

const isReadonly = computed(() => props.readonly);
const inputValue = ref(''); // CHANGED: 초기값은 watch에서 설정
const ValidationMsg = ref('');
const errMessage = ref(props.errmsg);
const isPopupVisible = ref(false);
const commonCodes = computed(() => useCommonCodeStore().getCommonCodes);
const toastComponentRef = ref(null);
const rentalName = ref('');
const vehicleData = ref([]);
const filteredOptions = ref([]);
const searchQuery = ref('');
const selectedKey = ref(String(props.modelValue ?? ''));

const makeKey = (vehicleType, vehicleModel) => `${vehicleType || ''}||${vehicleModel || ''}`;

// ADDED: 콤마를 제거하는 헬퍼 함수
const unformatValue = (value) => {
    return String(value || '').replace(/,/g, '');
};

const formatCurrency = (value) => {
    const v = unformatValue(value);
    if (v === '' || v == null) return '';
    const num = Number(v);
    if (!Number.isFinite(num)) return '';
    return num.toLocaleString();
};
const isSelected = (row) => selectedKey.value === row.key;

const filterOptions = () => {
    const query = (searchQuery.value || '').trim().toLowerCase();
    const queryDigits = query.replace(/\D/g, '');

    filteredOptions.value = (vehicleData.value || []).filter((row) => {
        const searchFields = [row.vehicle_type_name, row.vehicle_model, row.vehicle_grade, row.formula, String(row.original_price), String(row.calculate_price)].map((field) => (field || '').toLowerCase());
        const textMatch = searchFields.some((field) => field.includes(query));
        const numberMatch =
            queryDigits &&
            (String(row.original_price || '')
                .replace(/\D/g, '')
                .includes(queryDigits) ||
                String(row.calculate_price || '')
                    .replace(/\D/g, '')
                    .includes(queryDigits));
        return textMatch || numberMatch;
    });
};

const resetData = () => {
    vehicleData.value = [];
    filteredOptions.value = [];
    rentalName.value = '요금';
};

const findMatchByKeyOrPrice = (val, data = vehicleData.value) => {
    if (!data || !data.length) return null;

    const raw = String(val ?? '').trim();
    if (!raw) return null;

    const byKey = data.find((d) => d.key === raw);
    if (byKey) return byKey;

    const n = Number(unformatValue(raw));
    if (!Number.isFinite(n) || n <= 0) return null;
    if (Number.isFinite(n)) {
        const same = data.filter((d) => Number(d.calculate_price) === n);

        if (same.length === 1) return same[0];

        if (same.length > 1) {
            const preferKey = makeKey(props.selectedVehicleType, props.selectedVehicleModel);
            return same.find((d) => d.key === preferKey) ?? same[0];
        }
    }
    return null;
};

const handleAutoSelection = (data) => {
    if (!data?.length) return;

    if (selectedKey.value) {
        const cur = data.find((d) => d.key === selectedKey.value);
        if (cur) {
            applySelection(cur, { closePopup: false, isAutoSelect: true });
            return;
        }
    }

    if (props.selectedVehicleType || props.selectedVehicleModel) {
        const key = makeKey(props.selectedVehicleType, props.selectedVehicleModel);
        const byKey = data.find((d) => d.key === key);
        if (byKey) {
            applySelection(byKey, { closePopup: false, isAutoSelect: true });
            return;
        }
    }

    const raw = String(props.modelValue ?? '').trim();
    if (raw && Number(unformatValue(raw)) > 0) {
        const byPrice = findMatchByKeyOrPrice(props.modelValue, data);
        if (byPrice) {
            applySelection(byPrice, { closePopup: false, isAutoSelect: true });
        }
    }
};

let reqId = 0;

const fetchInsuranceRentalList = async () => {
    const contractNo = String(props.contractNo ?? '').trim();
    if (!contractNo) {
        resetData();
        return;
    }
    console.log(props.contractNo, ' , ', props.chargeRatio);

    const myId = ++reqId;
    const config = {
        method: 'post'
    };

    try {
        const { execute } = useAxios(`/api/contract/insurance/cost`, config, {
            immediate: false,
            onSuccess(response) {
                if (myId !== reqId) return;
                const data = response?.data?.data || {};
                const list = Array.isArray(data) ? data : [];

                rentalName.value = list.length > 0 ? String(list[0].rental_name ?? '요금') : '요금';

                const mappedData = list.map((item) => {
                    const vehicleType = String(item?.vehicle_type ?? '');
                    const vehicleModel = String(item?.vehicle_model ?? '');

                    return {
                        key: makeKey(vehicleType, vehicleModel),
                        vehicle_type: vehicleType,
                        vehicle_model: vehicleModel,
                        vehicle_type_name: String(item?.vehicle_type_name || item?.vehicle_type_name || ''),
                        vehicle_grade: String(item?.vehicle_grade ?? ''),
                        formula: String(item?.formula ?? ''),
                        original_price: Number(item?.original_price ?? 0),
                        calculate_price: Number(item?.calculate_price ?? 0)
                    };
                });

                vehicleData.value = mappedData;
                filteredOptions.value = [...mappedData];
                handleAutoSelection(mappedData);
            },
            onError(err) {
                if (myId !== reqId) return;
                console.error('API Error:', err?.message || err);
                resetData();
            }
        });
        console.log('execute before');
        await execute({
            contract_no: props.contractNo,
            charge_ratio: props.chargeRatio,
            pickup_date: props.vehicle.pickup_date,
            pickup_time: props.vehicle.pickup_time,
            return_expected_date: props.vehicle.return_expected_date,
            return_expected_time: props.vehicle.return_expected_time
        });
    } catch (err) {
        if (myId !== reqId) return;
        console.log(err);
        resetData();
    }
};

// const fetchInsuranceRentalList = async () => {
//     const contractNo = String(props.contractNo ?? '').trim();
//     if (!contractNo) {
//         resetData();
//         return;
//     }
//     console.log(props.contractNo, ' , ', props.chargeRatio);

//     const myId = ++reqId;
//     const config = {
//         method: 'post'
//     };

//     try {
//         const { execute } = useAxios('/api/contract/insurance/cost', config, {
//             immediate: false,
//             onSuccess(response) {
//                 if (myId !== reqId) return;

//                 const data = response?.data?.data || {};
//                 rentalName.value = String(data.rental_name || '요금');
//                 const mappedData = (data.vehicle_data_list || []).map((item, idx) => {
//                     const vehicleType = String(item?.vehicle_type || '');
//                     const vehicleModel = String(item?.vehicle_model || '');
//                     return {
//                         key: makeKey(vehicleType, vehicleModel),
//                         vehicle_type: vehicleType,
//                         vehicle_type_name: String(item?.vehicle_type_name || item?.vehicle_type_name || ''),
//                         vehicle_model: vehicleModel,
//                         vehicle_grade: String(item?.vehicle_grade || ''),
//                         fomula: String(item?.fomula || item?.formula || ''),
//                         original_price: Number(item?.original_price || 0),
//                         calculate_price: Number(item?.calculate_price || 0)
//                     };
//                 });
//                 vehicleData.value = mappedData;
//                 filteredOptions.value = mappedData;
//                 handleAutoSelection(mappedData);
//             },
//             onError(err) {
//                 if (myId !== reqId) return;
//                 console.error('API Error:', err?.message || err);
//                 resetData();
//             }
//         });
//         await execute({ contract_no: props.contractNo, charge_ratio: props.chargeRatio });
//     } catch (err) {
//         if (myId !== reqId) return;
//         console.log(err);
//         resetData();
//     }
// };

const ready = computed(() => String(props.contractNo ?? '').trim() !== '');

const costSearchKey = computed(() =>
    [props.contractNo ?? '', props.chargeRatio ?? '', props.vehicle?.pickup_date ?? '', props.vehicle?.pickup_time ?? '', props.vehicle?.return_expected_date ?? '', props.vehicle?.return_expected_time ?? ''].join('|')
);

watch(costSearchKey, () => fetchInsuranceRentalList(), { immediate: true });

onMounted(async () => {
    //await fetchInsuranceRentalList();
    // const mock = {
    //     rental_name: '1~2일 비용',
    //     vehicle_data: [
    //         {
    //             vehicle_type: 'SEDN',
    //             vehicle_type_name: '승용',
    //             vehicle_model: 'MINI COOPER / BENZ A220 / JEEP RENEGADE',
    //             vehicle_grade: '준중형',
    //             fomula: '((262000 * 0) + (262000 * (23 / 24))) * 0.70 = 175750',
    //             original_price: 262000,
    //             calculate_price: 175750
    //         },
    //         {
    //             vehicle_type: 'SEDN',
    //             vehicle_type_name: '승용',
    //             vehicle_model: 'BMW 520i / BENZ E250 / GENESIS G80',
    //             vehicle_grade: '대형',
    //             fomula: '((350000 * 0) + (350000 * (23 / 24))) * 0.65 = 219531',
    //             original_price: 350000,
    //             calculate_price: 219500
    //         },
    //         {
    //             vehicle_type: 'SUV',
    //             vehicle_type_name: 'SUV',
    //             vehicle_model: 'HYUNDAI SANTAFE / KIA SORENTO',
    //             vehicle_grade: '중형 SUV',
    //             fomula: '((310000 * 0) + (310000 * (23 / 24))) * 0.70 = 194875',
    //             original_price: 310000,
    //             calculate_price: 194800
    //         },
    //         {
    //             vehicle_type: 'EVCL',
    //             vehicle_type_name: '전기/수소차',
    //             vehicle_model: 'TESLA MODEL 3 / IONIQ 5',
    //             vehicle_grade: '중형 전기차',
    //             fomula: '((420000 * 0) + (420000 * (23 / 24))) * 0.80 = 321999',
    //             original_price: 420000,
    //             calculate_price: 322000
    //         },
    //         {
    //             vehicle_type: 'VAN',
    //             vehicle_type_name: '승합',
    //             vehicle_model: 'KIA CARNIVAL',
    //             vehicle_grade: '대형 VAN',
    //             fomula: '((380000 * 0) + (380000 * (23 / 24))) * 0.75 = 273437',
    //             original_price: 380000,
    //             calculate_price: 273400
    //         }
    //     ]
    // };
    // rentalName.value = String(mock.rental_name || '요금');
    // const mapped = mock.vehicle_data.map((item) => {
    //     const vehicleType = String(item?.vehicle_type || '');
    //     const vehicleModel = String(item?.vehicle_model || '');
    //     return {
    //         key: makeKey(vehicleType, vehicleModel),
    //         vehicle_type: vehicleType,
    //         vehicle_type_name: String(item?.vehicle_type_name || ''),
    //         vehicle_model: vehicleModel,
    //         vehicle_grade: String(item?.vehicle_grade || ''),
    //         fomula: String(item?.fomula || item?.formula || ''),
    //         original_price: Number(item?.original_price || 0),
    //         calculate_price: Number(item?.calculate_price || 0)
    //     };
    // });
    // vehicleData.value = mapped;
    // filteredOptions.value = mapped;
    // handleAutoSelection(mapped);
});

watch(
    () => [props.selectedVehicleType, props.selectedVehicleModel],
    ([newType, newModel]) => {
        if (!vehicleData.value?.length) return;
        if (!newType && !newModel) return;
        const targetKey = makeKey(newType, newModel);
        const foundItem = vehicleData.value.find((item) => item.key === targetKey);
        if (foundItem) {
            applySelection(foundItem, { closePopup: false, isAutoSelect: true });
        }
    }
);

watch(
    () => props.modelValue,
    (newVal) => {
        if (newVal === '' || newVal == null) {
            selectedKey.value = '';
            inputValue.value = '';
            return;
        }

        const match = findMatchByKeyOrPrice(newVal);

        if (match) {
            if (selectedKey.value !== match.key) {
                applySelection(match, { closePopup: false, isAutoSelect: true });
            } else {
                inputValue.value = formatCurrency(match.calculate_price);
            }
            return;
        }

        selectedKey.value = String(newVal ?? '');
        inputValue.value = formatCurrency(newVal);
    },
    { immediate: true }
);

watch(
    () => props.errmsg,
    (newMsg) => {
        ValidationMsg.value = newMsg || '';
        errMessage.value = newMsg || '';
    },
    { immediate: true }
);

const onInput = (event) => {
    const { value, selectionStart } = event.target;

    const rawValue = unformatValue(value);

    if (isNaN(Number(rawValue))) {
        event.target.value = inputValue.value;
        return;
    }

    const formattedValue = formatCurrency(rawValue);
    const originalLength = value.length;
    const newLength = formattedValue.length;
    let newCursorPosition = selectionStart + (newLength - originalLength);

    emit('update:modelValue', rawValue);
    emit('input', {
        id: props.inputId,
        value: rawValue,
        isSelected: false
    });

    inputValue.value = formattedValue;

    nextTick(() => {
        event.target.setSelectionRange(newCursorPosition, newCursorPosition);
    });
};

const emitInputUpdate = (value) => {};

const togglePopup = () => {
    if (props.disabled || props.readonly) return;

    if (!props.chargeRatio || props.chargeRatio === '') {
        toastComponentRef.value?.showToast('info', '알림', '청구율을 선택하세요.');
        return;
    }

    isPopupVisible.value = !isPopupVisible.value;
};

const closePopup = () => {
    isPopupVisible.value = false;
};

watch(isPopupVisible, (isOpen) => {
    if (isOpen) {
        searchQuery.value = '';
        filteredOptions.value = vehicleData.value || [];
    }
});

const applySelection = (option, { closePopup = true, isAutoSelect = false } = {}) => {
    selectedKey.value = option.key;

    const displayText = formatCurrency(option.calculate_price);
    inputValue.value = displayText;

    const payload = {
        id: props.inputId,
        key: option.key,
        value: option.key,
        displayValue: unformatValue(displayText),
        vehicle_type: option.vehicle_type,
        vehicle_type_name: option.vehicle_type_name,
        vehicle_model: option.vehicle_model,
        vehicle_grade: option.vehicle_grade,
        formula: option.formula,
        original_price: option.original_price,
        calculate_price: option.calculate_price,
        charge_ratio: props.chargeRatio,
        rental_name: rentalName.value,
        isSelected: true,
        isAutoSelect
    };

    if (!isAutoSelect) {
        emit('update:modelValue', String(option.calculate_price));
        emit('input', payload);
        emit('change', payload);
    }

    ValidationMsg.value = '';
    if (closePopup) {
        isPopupVisible.value = false;
    }
};

const selectRow = (option) => {
    applySelection(option, { closePopup: true, isAutoSelect: false });
};

const clearInput = () => {
    selectedKey.value = '';
    inputValue.value = '';

    const clearPayload = {
        id: props.inputId,
        value: '',
        key: '',
        isSelected: false
    };

    emit('update:modelValue', '');
    emit('input', clearPayload);
    emit('change', clearPayload);
    ValidationMsg.value = '';
};

const inputRightPadding = computed(() => {
    return inputValue.value ? 'calc(6.5rem + 1.5rem + 2rem)' : '6.5rem';
});
</script>

<template>
    <ToastComponent ref="toastComponentRef" />
    <div
        class="flex flex-col"
        :style="{
            marginTop: marginTop,
            marginBottom: marginBottom,
            marginLeft: marginLeft,
            marginRight: marginRight
        }"
    >
        <label v-if="!labelEmpty" class="label">
            {{ label }}
            <span v-if="required" class="required-asterisk">*</span>
            <span v-if="ValidationMsg" class="validation-message">{{ ValidationMsg }}</span>
        </label>

        <div class="relative flex items-center w-full h-12">
            <input
                v-if="visible"
                type="text"
                :id="inputId"
                v-model="inputValue"
                class="p-inputtext block w-full h-[2.5rem] ps-5 input-font text-gray-900 border border-gray-300 rounded-lg bg-gray-60 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                :class="{
                    'error-border': ValidationMsg,
                    'readonly-Bg': isReadonly
                }"
                :style="{ paddingRight: inputRightPadding }"
                :readonly="isReadonly"
                :placeholder="placeholder"
                :required="required"
                :disabled="disabled"
                @input="onInput"
            />

            <div class="absolute right-0 top-1/2 transform -translate-y-1/2 flex items-center">
                <button v-if="inputValue && !isReadonly && !disabled" @click="clearInput" class="clear-button mr-[calc(6.5rem)]" type="button">&times;</button>

                <button v-if="!isReadonly && !disabled" @click="togglePopup" class="button-style" type="button">청구기준 설정</button>

                <span class="unit-text">원</span>
            </div>
        </div>

        <Teleport to="body">
            <div v-if="isPopupVisible" class="popup-overlay" @click="closePopup">
                <div class="popup-content" @click.stop>
                    <input v-model="searchQuery" @input="filterOptions" placeholder="차량 종류, 등급, 모델명, 가격 등으로 검색" class="search-input" />

                    <div class="options-container">
                        <table class="vehicle-grid">
                            <thead>
                                <tr>
                                    <th style="width: 4rem">선택</th>
                                    <th style="width: 6rem">차량종류</th>
                                    <th style="width: 7rem">차량등급</th>
                                    <th style="width: 20rem">모델명</th>
                                    <th style="width: 8rem">{{ rentalName || '요금' }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="option in filteredOptions" :key="option.key" @click="selectRow(option)" class="popup-item" :class="{ 'selected-row': isSelected(option) }">
                                    <td @click.stop>
                                        <input type="radio" :name="`${inputId || 'vehicle'}`" :value="option.key" :checked="isSelected(option)" @change="selectRow(option)" />
                                    </td>
                                    <td>{{ option.vehicle_type_name }}</td>
                                    <td>{{ option.vehicle_grade }}</td>
                                    <td>{{ option.vehicle_model }}</td>
                                    <td>{{ formatCurrency(option.calculate_price) }} 원</td>
                                </tr>

                                <tr v-if="!filteredOptions.length">
                                    <td colspan="5" class="no-data">검색 결과가 없습니다.</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <button @click="closePopup" class="close-button w-full" type="button">닫기</button>
                </div>
            </div>
        </Teleport>
    </div>
</template>

<style scoped>
.label {
    font-family: 'SCoreDream600';
    display: flex;
    align-items: center;
    margin-bottom: 0.25rem;
    margin-left: 0.25rem;
    height: 1.3rem;
}

.required-asterisk {
    color: #f87171;
    margin-left: 0.2rem;
    position: relative;
    top: -0.2rem;
}

.input-font {
    font-family: 'SCoreDream400';
    font-size: 0.9rem;
}

input:focus:not([readonly]) {
    border-color: #0038ea;
    outline: none;
}

input[readonly]:focus {
    border-color: #d1d5db;
    outline: none;
}

.error-border {
    border: 1px solid #ef4444 !important;
}

.readonly-Bg {
    background-color: #f9fafb !important;
}

.validation-message {
    color: #ef4444;
    font-size: 0.75rem;
    margin-left: 0.5rem;
}

.button-style {
    font-family: 'SCoreDream500';
    font-size: 0.85rem;
    transition: background-color 0.3s ease;
    border-radius: 0.5rem;
    background-color: #f1f5f9;
    color: #475569;
    position: absolute;
    right: 0.5rem;
    width: 6rem;
    height: 1.8rem;
    border: none;
    cursor: pointer;
}

.button-style:hover {
    background-color: #e2e8f0;
}

.button-style:focus {
    outline: none;
    box-shadow: 0 0 0 2px #3b82f6;
}

.clear-button {
    cursor: pointer;
    background: transparent;
    border: none;
    font-size: 0.9rem;
    color: #6b7280;
    position: absolute;
    right: 0.3rem;
}

.clear-button:hover {
    color: #4b5563;
}

.unit-text {
    position: absolute;
    right: 8.5rem;
    display: flex;
    align-items: center;
    height: 100%;
    font-family: 'SCoreDream500';
    font-size: 0.875rem;
    color: #6b7280;
    user-select: none;
}

.popup-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9999;
    user-select: none;
}

.popup-content {
    background: white;
    padding: 1rem;
    border-radius: 0.75rem;
    width: 50rem;
    max-height: 80vh;
    display: flex;
    flex-direction: column;
}

.search-input {
    width: 100%;
    padding: 0.75rem;
    margin-bottom: 1rem;
    border: 1px solid #d1d5db;
    border-radius: 0.5rem;
    font-family: 'SCoreDream400';
    font-size: 0.9rem;
}

.search-input:focus {
    border-color: #3b82f6;
    outline: none;
}

.options-container {
    flex-grow: 1;
    height: 25rem;
    overflow-y: auto;
}

.vehicle-grid {
    width: 100%;
    border-collapse: collapse;
}

.vehicle-grid th,
.vehicle-grid td {
    border: 1px solid #e5e7eb;
    padding: 0.5rem 1rem;
    font-family: 'SCoreDream400';
    font-size: 0.875rem;
    vertical-align: middle;
}

.vehicle-grid th {
    background-color: #f8fafc;
    font-family: 'SCoreDream600';
    position: sticky;
    top: 0;
    z-index: 10;
    text-align: center;
}

.vehicle-grid td {
    text-align: left;
}

.vehicle-grid .text-left {
    text-align: left;
}

.vehicle-grid .text-right {
    text-align: right;
}

.price-cell {
    font-family: 'SCoreDream500';
    color: #059669;
}

.popup-item {
    cursor: pointer;
    transition: background-color 0.2s;
}

.popup-item:hover {
    background-color: #f8fafc;
}

.selected-row {
    background-color: #eff6ff !important;
}

.selected-row:hover {
    background-color: #dbeafe !important;
}

.no-data {
    text-align: center;
    color: #6b7280;
    font-style: italic;
    padding: 2rem;
}

.close-button {
    font-family: 'SCoreDream500';
    font-size: 0.9rem;
    margin-top: 1rem;
    background: #f1f5f9;
    color: #475569;
    padding: 0.75rem 1.5rem;
    border: none;
    border-radius: 0.5rem;
    cursor: pointer;
    align-self: center;
}

.close-button:hover {
    background: #e2e8f0;
}

.options-container::-webkit-scrollbar {
    width: 8px;
}

.options-container::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 4px;
}

.options-container::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 4px;
}

.options-container::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
}

input[type='checkbox'] {
    width: 16px;
    height: 16px;
    cursor: pointer;
}

.text-right::-webkit-input-placeholder {
    text-align: left !important;
}
.text-right::-moz-placeholder {
    text-align: left !important;
}
.text-right:-ms-input-placeholder {
    text-align: left !important;
}
.text-right:-moz-placeholder {
    text-align: left !important;
}
</style>
